import { useEffect, useMemo, useState } from "react";

import type { RecordingState } from "~desktop/shared/desktop-recorder-types";

import LocalRecordingManager, { StatusChangedEvent } from "./LocalRecordingManager";

export interface RecordingStateWithDerived {
	/** The raw recording state */
	state: RecordingState;
	/** True when recording is actively running */
	isRecording: boolean;
	/** True when recording is paused */
	isPaused: boolean;
	/** True when microphone is muted (only applies when not idle) */
	isMuted: boolean;
	/** True when recording is idle (not started) */
	isIdle: boolean;
	/** True when recording is active (either recording or paused) */
	isActive: boolean;
	/** Meeting recording ID if available */
	meetingRecordingId?: string;
}

/**
 * React hook that tracks the current state of the LocalRecordingManager and
 * forces a re-render whenever that state changes. Returns both the raw state
 * and common derived interpretations.
 */
export function useRecordingState(): RecordingStateWithDerived {
	const [state, setState] = useState<RecordingState>(() => {
		// Attempt to synchronously retrieve the current state if the helper exists.
		// We purposely guard with optional chaining to avoid runtime errors if the
		// method is not yet available (e.g. hot-reload with stale bundle).
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const initial = (LocalRecordingManager as any).getCurrentState?.() as RecordingState | undefined;
		return initial ?? { status: "idle" };
	});

	useEffect(() => {
		const handleStateChanged = (event: StatusChangedEvent) => {
			setState(event.state);
		};

		LocalRecordingManager.addEventListener("stateChanged", handleStateChanged);

		return () => {
			LocalRecordingManager.removeEventListener("stateChanged", handleStateChanged);
		};
	}, []);

	const derived = useMemo((): RecordingStateWithDerived => {
		const isRecording = state.status === "recording";
		const isPaused = state.status === "paused";
		const isIdle = state.status === "idle";
		const isActive = !isIdle;
		const isMuted = isActive && "muted" in state && state.muted;
		const meetingRecordingId = "meetingRecordingId" in state ? state.meetingRecordingId : undefined;

		return {
			state,
			isRecording,
			isPaused,
			isMuted,
			isIdle,
			isActive,
			meetingRecordingId,
		};
	}, [state]);

	return derived;
}
